import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, FileCheck2, ScanSearch, MonitorCog, Scale, Stethoscope, Calculator, LineChart, HeartHandshake, Building2, Quote } from 'lucide-react';
import { home, site, nav } from '../mock';

const serviceIcon = {
  'virtual-ciso': ShieldCheck,
  'penetration-testing': ScanSearch,
  'patch-management-rmm': MonitorCog,
  'regulatory-compliance': Scale,
};

const audienceIcon = {
  'healthcare': Stethoscope,
  'tax-accounting': Calculator,
  'financial-insurance': LineChart,
  'nonprofits': HeartHandshake,
  'small-business': Building2,
};

const Home = () => {
  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative hero-gradient overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-[#1F3864] mb-6">
              <span className="inline-block w-8 h-px bg-[#1F3864]" />
              Since 2011 • Trent Woods, NC
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1F3864]">
              {home.hero.headline}
            </h1>
            <p className="mt-7 text-[17px] md:text-[19px] leading-relaxed text-slate-700 max-w-2xl">
              {home.hero.sub}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/contact" className="group inline-flex items-center gap-2 bg-[#1F3864] text-white text-[15px] font-medium px-6 py-3.5 rounded-sm hover:bg-[#17294a] transition-colors">
                {home.hero.primaryCta}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/who-we-serve" className="inline-flex items-center gap-2 text-[15px] font-medium text-[#1F3864] hover:text-[#2E75B6] px-2 py-2">
                {home.hero.secondaryCta}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Small credentials strip */}
          <div className="mt-16 border-t border-slate-200 pt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px] uppercase tracking-[0.14em] text-slate-500">
            <span>HIPAA Security Rule</span>
            <span className="text-slate-300">•</span>
            <span>FTC Safeguards</span>
            <span className="text-slate-300">•</span>
            <span>SEC Reg S-P</span>
            <span className="text-slate-300">•</span>
            <span>NAIC Model Law</span>
            <span className="text-slate-300">•</span>
            <span>NYDFS Part 500</span>
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="rule-navy mb-6" />
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight text-[#1F3864]">
              {home.value.heading}
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[16px] leading-[1.75] text-slate-700">
            {home.value.paragraphs.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 py-24 md:py-28 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="rule-navy mb-6" />
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1F3864] tracking-tight">{home.services.heading}</h2>
            <p className="mt-4 text-slate-600 text-[16px]">Every engagement is scoped, executed, and signed by Andrew Garlick.</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {home.services.items.map(s => {
              const Icon = serviceIcon[s.slug] || FileCheck2;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="card-hover group block bg-white border border-slate-200 rounded-sm p-7">
                  <div className="w-11 h-11 rounded-sm bg-[#1F3864]/5 text-[#1F3864] flex items-center justify-center mb-5 group-hover:bg-[#1F3864] group-hover:text-white transition-colors">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <div className="font-serif-display text-[20px] leading-snug text-[#1F3864] mb-3">{s.title}</div>
                  <p className="text-[14.5px] leading-relaxed text-slate-600">{s.short}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#2E75B6] group-hover:gap-2.5 transition-all">
                    Learn more <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <div className="rule-navy mb-6" />
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1F3864] tracking-tight">Who We Serve</h2>
            </div>
            <div className="md:col-span-5 text-slate-600 text-[15.5px] leading-relaxed">
              Our engagements are structured around the specific regulatory regime that governs your practice — or, for unregulated small businesses, the security posture your insurer, customers, and lenders now expect.
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {nav.audiences.map((a, idx) => {
              const Icon = audienceIcon[a.slug];
              return (
                <Link key={a.slug} to={`/who-we-serve/${a.slug}`} className="card-hover group block bg-white border border-slate-200 rounded-sm p-7 relative overflow-hidden">
                  <div className="absolute top-4 right-4 text-[11px] tracking-widest text-slate-400">0{idx + 1}</div>
                  <Icon size={26} strokeWidth={1.5} className="text-[#1F3864] mb-6" />
                  <div className="font-serif-display text-[19px] leading-snug text-[#1F3864] mb-2">{a.title}</div>
                  <div className="h-px w-8 bg-[#1F3864]/30 my-4 group-hover:w-16 group-hover:bg-[#1F3864] transition-all" />
                  <div className="text-[13.5px] font-medium text-[#2E75B6] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">Explore <ArrowRight size={14} /></div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CREDENTIALS STRIP */}
      <section className="bg-[#1F3864] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
          <Quote size={28} className="mx-auto text-white/40 mb-6" strokeWidth={1.4} />
          <p className="font-serif-display text-2xl md:text-3xl leading-snug tracking-tight max-w-4xl mx-auto">
            The same discipline that protects a multinational can protect a six-person dental practice — provided it is properly scaled, correctly priced, and actually delivered.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] uppercase tracking-[0.14em] text-white/80">
            <span>Andrew M. Garlick</span>
            <span className="text-white/30">|</span>
            <span>MBA, PMP, HRIP</span>
            <span className="text-white/30">|</span>
            <span>Former CIO, Young &amp; Rubicam</span>
            <span className="text-white/30">|</span>
            <span>SVP, WPP Group USA</span>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="rule-navy mx-auto mb-6" />
          <h2 className="font-serif-display text-3xl md:text-5xl text-[#1F3864] tracking-tight">{home.closing.heading}</h2>
          <p className="mt-6 text-[17px] leading-relaxed text-slate-700 max-w-2xl mx-auto">{home.closing.body}</p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={`mailto:${site.email}`} className="inline-flex items-center gap-2 bg-[#1F3864] text-white text-[15px] font-medium px-6 py-3.5 rounded-sm hover:bg-[#17294a] transition-colors">
              Email {site.email}
            </a>
            <a href={site.phoneHref} className="inline-flex items-center gap-2 border border-[#1F3864] text-[#1F3864] text-[15px] font-medium px-6 py-3.5 rounded-sm hover:bg-[#1F3864] hover:text-white transition-colors">
              Call {site.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
